import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";
import "../styles/Dashboard.css";
import "../styles/WeeklyReport.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const WeeklyReport = ({ username }) => {
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const yearOptions = useMemo(() => {
        return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }, [currentYear]);

    const getToken = () => {
        const token = localStorage.getItem("token");
        return token ? `Bearer ${token}` : null;
    };

    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            window.location.href = "/login";
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await axios.get(
                `${API_BASE_URL}/reports/${username}/weekly?year=${selectedYear}`,
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            );
            setData(res.data);
        } catch (err) {
            console.error("❌ Error fetching weekly data:", err);
            setError("Gagal mengambil data mingguan.");
        } finally {
            setLoading(false);
        }
    }, [username, selectedYear]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const groupedData = useMemo(() => {
        if (!data?.weekly) return [];

        return data.weekly.map((d) => {
            const weekStart = new Date(d.week_start);
            const weekEnd = new Date(d.week_end);

            return {
                weekNumber: Number(d.week),
                weekStart,
                weekEnd,
                week: `Minggu ${d.week}`,
                period: `${weekStart.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                })} - ${weekEnd.toLocaleDateString("id-ID", {
                    day: "2-digit",
                    month: "short",
                })}`,
                volume: parseFloat(d.volume) || 0,
                gallons: parseFloat(d.gallons) || 0,
            };
        });
    }, [data]);

    const sortedData = useMemo(
        () => [...groupedData].sort((a, b) => a.weekStart - b.weekStart),
        [groupedData]
    );

    const latestWeekNumber =
        sortedData.length > 0 ? Math.max(...sortedData.map((d) => d.weekNumber)) : null;

    const summary = useMemo(() => {
        const totalVolume = sortedData.reduce((sum, item) => sum + item.volume, 0);
        const totalGallons = sortedData.reduce((sum, item) => sum + item.gallons, 0);
        return {
            totalVolume,
            totalGallons,
            totalWeeks: sortedData.length,
        };
    }, [sortedData]);

    const isDataEmpty = sortedData.length === 0;

    const columns = useMemo(
        () => [
            { Header: "Minggu", accessor: "week" },
            { Header: "Periode", accessor: "period" },
            {
                Header: "Volume (L)",
                accessor: "volume",
                Cell: ({ value }) => Number(value).toFixed(2),
            },
            { Header: "Total Galon", accessor: "gallons" },
        ],
        []
    );

    const tableInstance = useTable({ columns, data: sortedData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    return (
        <div className="report-card-shell">
            <div className="report-topbar">
                <div>
                    <h3 className="report-title">Laporan Mingguan</h3>
                    <p className="report-subtitle">
                        Rekap penggunaan air mingguan untuk tahun {selectedYear}.
                    </p>
                </div>

                <button className="refresh-btn" onClick={fetchData}>
                    Refresh Data
                </button>
            </div>

            <div className="report-filters">
                <div className="form-group">
                    <label>Tahun</label>
                    <select
                        className="report-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="report-empty">Belum ada data mingguan untuk tahun {selectedYear}.</p>
            ) : (
                <>
                    <div className="report-summary-grid">
                        <div className="report-summary-card">
                            <span className="summary-label">Total Volume</span>
                            <strong className="summary-value">
                                {summary.totalVolume.toFixed(2)} L
                            </strong>
                            <small className="summary-subvalue">Tahun {selectedYear}</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Total Galon</span>
                            <strong className="summary-value">{summary.totalGallons}</strong>
                            <small className="summary-subvalue">Akumulasi mingguan</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Jumlah Minggu</span>
                            <strong className="summary-value">{summary.totalWeeks}</strong>
                            <small className="summary-subvalue">Minggu yang memiliki data</small>
                        </div>
                    </div>

                    <div className="chart-container">
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="360px"
                            data={[
                                ["Minggu", "Volume (L)", "Galon"],
                                ...sortedData.map((d) => [d.week, Number(d.volume), Number(d.gallons)]),
                            ]}
                            options={{
                                title: `Trend Penggunaan Air Mingguan - ${selectedYear}`,
                                curveType: "function",
                                legend: { position: "top" },
                                colors: ["#0ea5e9", "#22c55e"],
                                series: {
                                    0: { targetAxisIndex: 0 },
                                    1: { targetAxisIndex: 1 },
                                },
                                vAxes: {
                                    0: { title: "Volume (L)" },
                                    1: { title: "Galon" },
                                },
                                chartArea: {
                                    left: 55,
                                    top: 55,
                                    width: "88%",
                                    height: "72%",
                                },
                            }}
                        />
                    </div>

                    <div className="dashboard-table-wrapper">
                        <table {...getTableProps()} className="dashboard-table">
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                        {headerGroup.headers.map((column) => (
                                            <th {...column.getHeaderProps()} key={column.id}>
                                                {column.render("Header")}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr
                                            {...row.getRowProps()}
                                            key={row.id}
                                            className={
                                                row.original.weekNumber === latestWeekNumber
                                                    ? "latest-week"
                                                    : ""
                                            }
                                        >
                                            {row.cells.map((cell) => (
                                                <td {...cell.getCellProps()} key={cell.column.id}>
                                                    {cell.render("Cell")}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default WeeklyReport;